import React, { useState, useEffect, useRef } from 'react';
import {
  listarClientes, inserirCliente, atualizarCliente, excluirCliente,
  uploadFotoCliente, listarFotosCliente, getUrlFoto, excluirFotoCliente,
} from '../lib/supabase';
import { Plus, Search, Edit2, Trash2, X, User, Phone, MapPin, FileText, Camera } from 'lucide-react';

const FORM_VAZIO = { tipo: 'Cliente', nome: '', cpf: '', telefone: '', endereco: '', cidade: '', observacao: '' };
const TIPOS = ['Cliente', 'Proprietário', 'Ambos'];

function fmtCPF(v) {
  return v.replace(/\D/g,'').slice(0,11).replace(/(\d{3})(\d)/,'$1.$2').replace(/(\d{3})(\d)/,'$1.$2').replace(/(\d{3})(\d{1,2})$/,'$1-$2');
}
function fmtTel(v) {
  const d = v.replace(/\D/g,'').slice(0,11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/,'($1) $2-$3');
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/,'($1) $2-$3');
}

export default function Clientes() {
  const [clientes,    setClientes]    = useState([]);
  const [busca,       setBusca]       = useState('');
  const [loading,     setLoading]     = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando,    setEditando]    = useState(null);
  const [form,        setForm]        = useState(FORM_VAZIO);
  const [fotos,       setFotos]       = useState([]);
  const [salvando,    setSalvando]    = useState(false);
  const [excluindo,   setExcluindo]   = useState(null);
  const [fotoModal,   setFotoModal]   = useState(null);
  const fileRef = useRef();

  const carregar = async () => {
    setLoading(true);
    const data = await listarClientes();
    setClientes(data || []);
    setLoading(false);
  };

  useEffect(() => { carregar(); }, []);

  const abrirNovo = () => { setEditando(null); setForm(FORM_VAZIO); setFotos([]); setModalAberto(true); };
  const abrirEditar = async (c) => {
    setEditando(c.id);
    setForm({ tipo: c.tipo||'Cliente', nome: c.nome||'', cpf: c.cpf||'', telefone: c.telefone||'', endereco: c.endereco||'', cidade: c.cidade||'', observacao: c.observacao||'' });
    const fs = await listarFotosCliente(c.id);
    setFotos(fs || []);
    setModalAberto(true);
  };
  const fecharModal = () => { setModalAberto(false); setEditando(null); setFotos([]); };

  const handleField = (e) => {
    const { name, value } = e.target;
    if (name === 'cpf')      return setForm(f => ({ ...f, cpf: fmtCPF(value) }));
    if (name === 'telefone') return setForm(f => ({ ...f, telefone: fmtTel(value) }));
    setForm(f => ({ ...f, [name]: value }));
  };

  const salvar = async () => {
    if (!form.nome) return alert('Nome é obrigatório.');
    if (!editando && fotos.length === 0) return alert('Adicione pelo menos 1 documento (CNH ou comprovante de endereço).');
    setSalvando(true);
    try {
      let clienteId = editando;
      if (editando) { await atualizarCliente(editando, form); }
      else {
        const novo = await inserirCliente(form);
        clienteId = novo?.id;
      }
      if (clienteId) {
        for (const f of fotos) {
          if (f instanceof File) await uploadFotoCliente(clienteId, f, 'documento');
        }
      }
      await carregar();
      fecharModal();
    } catch (err) {
      alert('Erro ao salvar: ' + (err.message || err));
    } finally { setSalvando(false); }
  };

  const confirmarExcluir = async (id) => {
    if (!window.confirm('Excluir este cliente?')) return;
    setExcluindo(id);
    await excluirCliente(id);
    await carregar();
    setExcluindo(null);
  };

  const removerFoto = async (foto, idx) => {
    if (foto instanceof File) { setFotos(p => p.filter((_, i) => i !== idx)); }
    else { await excluirFotoCliente(foto.id, foto.caminho); setFotos(p => p.filter((_, i) => i !== idx)); }
  };

  const fotoUrl = (f) => f instanceof File ? URL.createObjectURL(f) : getUrlFoto(f.caminho);

  const clientesFiltrados = clientes.filter(c => {
    const q = busca.toLowerCase();
    return !busca || c.nome?.toLowerCase().includes(q) || c.cpf?.includes(q) || c.telefone?.includes(q) || c.cidade?.toLowerCase().includes(q);
  });

  const corTipo = { 'Proprietário': 'laranja', 'Ambos': 'azul', 'Cliente': 'verde' };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-sub">{clientes.length} cadastro{clientes.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={abrirNovo}><Plus size={18} /> Novo Cadastro</button>
      </div>

      <div className="filtros-bar">
        <div className="search-wrap">
          <Search size={16} className="search-icon" />
          <input className="search-input" placeholder="Buscar por nome, CPF, telefone ou cidade..." value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : clientesFiltrados.length === 0 ? (
        <div className="empty-state">
          <User size={48} strokeWidth={1} color="#CBD5E1" />
          <p>{busca ? 'Nenhum cadastro encontrado.' : 'Nenhum cadastro ainda.'}</p>
          {!busca && <button className="btn btn-primary" onClick={abrirNovo}><Plus size={16} /> Adicionar primeiro</button>}
        </div>
      ) : (
        <div className="clientes-lista">
          {clientesFiltrados.map(c => (
            <div key={c.id} className="cliente-card">
              <div className="cliente-avatar">{c.nome?.charAt(0).toUpperCase()}</div>
              <div className="cliente-info">
                <div className="cliente-nome-row">
                  <h3 className="cliente-nome">{c.nome}</h3>
                  <span className={`badge badge-${corTipo[c.tipo]||'cinza'}`}>{c.tipo || 'Cliente'}</span>
                </div>
                <div className="cliente-meta">
                  {c.cpf      && <span><User size={13} /> {c.cpf}</span>}
                  {c.telefone && <span><Phone size={13} /> {c.telefone}</span>}
                  {c.cidade   && <span><MapPin size={13} /> {c.cidade}</span>}
                </div>
                {c.observacao && <p className="cliente-obs">{c.observacao}</p>}
              </div>
              <div className="cliente-acoes">
                <button className="btn btn-ghost btn-sm" onClick={() => abrirEditar(c)}><Edit2 size={15} /></button>
                <button className="btn btn-ghost btn-sm btn-danger" onClick={() => confirmarExcluir(c.id)} disabled={excluindo === c.id}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalAberto && (
        <div className="modal-backdrop" onClick={fecharModal}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editando ? 'Editar Cadastro' : 'Novo Cadastro'}</h2>
              <button className="modal-close" onClick={fecharModal}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-section-title">Tipo de cadastro</div>
              <div className="tipo-toggle">
                {TIPOS.map(t => (
                  <button key={t} type="button"
                    className={`tipo-btn ${form.tipo === t ? 'tipo-btn--ativo' : ''}`}
                    onClick={() => setForm(f => ({ ...f, tipo: t }))}
                  >{t}</button>
                ))}
              </div>

              <div className="form-section-title" style={{marginTop:16}}>Dados pessoais</div>
              <div className="form-grid">
                <div className="form-group form-full"><label className="label">Nome completo *</label><input className="input-field" name="nome" value={form.nome} onChange={handleField} placeholder="Nome" /></div>
                <div className="form-group"><label className="label">CPF</label><input className="input-field" name="cpf" value={form.cpf} onChange={handleField} placeholder="000.000.000-00" maxLength={14} /></div>
                <div className="form-group"><label className="label">Telefone</label><input className="input-field" name="telefone" value={form.telefone} onChange={handleField} placeholder="(00) 00000-0000" maxLength={16} /></div>
                <div className="form-group form-full"><label className="label">Endereço</label><input className="input-field" name="endereco" value={form.endereco} onChange={handleField} placeholder="Rua, número, bairro" /></div>
                <div className="form-group"><label className="label">Cidade</label><input className="input-field" name="cidade" value={form.cidade} onChange={handleField} placeholder="Cidade" /></div>
                <div className="form-group form-full"><label className="label">Observação</label><textarea className="input-field textarea" name="observacao" value={form.observacao} onChange={handleField} placeholder="Anotações..." rows={2} /></div>
              </div>

              <div className="form-section-title">📄 Documentos <span className="obrigatorio">*obrigatório (CNH, comprovante de endereço...)</span></div>
              <div className="fotos-grid">
                {fotos.map((f, i) => {
                  const url = fotoUrl(f);
                  return (
                    <div key={i} className="foto-thumb">
                      <img src={url} alt="" onClick={() => setFotoModal(url)} />
                      <button className="foto-remove" onClick={() => removerFoto(f, i)}><X size={12} /></button>
                    </div>
                  );
                })}
                <button type="button" className="foto-add" onClick={() => fileRef.current?.click()}>
                  <FileText size={20} /><span>Adicionar</span>
                </button>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={e => setFotos(p => [...p, ...Array.from(e.target.files)])} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={fecharModal}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {fotoModal && <div className="modal-backdrop" onClick={() => setFotoModal(null)}><img src={fotoModal} alt="" style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:12}} /></div>}

      <style>{`
        .page { padding: 24px; }
        .page-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; flex-wrap:wrap; gap:12px; }
        .page-title { font-size:1.5rem; font-weight:700; color:var(--azul); margin:0; }
        .page-sub { color:#6B7280; font-size:.875rem; margin:2px 0 0; }
        .filtros-bar { display:flex; gap:12px; margin-bottom:20px; }
        .search-wrap { position:relative; flex:1; }
        .search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:#9CA3AF; }
        .search-input { width:100%; padding:9px 12px 9px 36px; border:1.5px solid #E5E7EB; border-radius:8px; font-size:.9rem; outline:none; transition:.15s; box-sizing:border-box; }
        .search-input:focus { border-color:var(--azul); }
        .clientes-lista { display:flex; flex-direction:column; gap:10px; }
        .cliente-card { background:#fff; border-radius:12px; border:1.5px solid #E5E7EB; padding:16px; display:flex; align-items:center; gap:14px; transition:.15s; }
        .cliente-card:hover { border-color:#CBD5E1; box-shadow:0 2px 8px rgba(0,0,0,.06); }
        .cliente-avatar { width:46px; height:46px; border-radius:50%; background:var(--azul); color:var(--amarelo); display:flex; align-items:center; justify-content:center; font-weight:700; font-size:1.1rem; flex-shrink:0; }
        .cliente-info { flex:1; min-width:0; }
        .cliente-nome-row { display:flex; align-items:center; gap:8px; margin-bottom:4px; }
        .cliente-nome { font-weight:700; color:var(--azul); margin:0; font-size:.95rem; }
        .cliente-meta { display:flex; flex-wrap:wrap; gap:10px; font-size:.8rem; color:#6B7280; }
        .cliente-meta span { display:flex; align-items:center; gap:4px; }
        .cliente-obs { font-size:.8rem; color:#9CA3AF; margin:4px 0 0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .cliente-acoes { display:flex; gap:4px; }
        .btn-ghost { background:transparent; border:1.5px solid #E5E7EB; color:#6B7280; }
        .btn-ghost:hover { background:#F9FAFB; }
        .btn-danger:hover { color:#EF4444 !important; border-color:#EF4444 !important; }
        .modal-lg { width:100%; max-width:640px; }
        .modal-header { display:flex; justify-content:space-between; align-items:center; padding:20px 24px 0; }
        .modal-header h2 { font-size:1.1rem; font-weight:700; color:var(--azul); }
        .modal-close { background:none; border:none; cursor:pointer; color:#9CA3AF; }
        .modal-body { padding:20px 24px; max-height:75vh; overflow-y:auto; }
        .modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #F1F5F9; }
        .form-section-title { font-size:.75rem; font-weight:700; text-transform:uppercase; letter-spacing:.08em; color:#9CA3AF; margin:16px 0 10px; }
        .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .form-group { display:flex; flex-direction:column; gap:4px; }
        .form-full { grid-column:1/-1; }
        .textarea { resize:vertical; min-height:60px; }
        .tipo-toggle { display:flex; gap:8px; }
        .tipo-btn { flex:1; padding:10px; border-radius:8px; border:1.5px solid #E5E7EB; background:#F8FAFC; font-weight:600; cursor:pointer; font-size:.875rem; transition:.15s; color:#6B7280; }
        .tipo-btn--ativo { background:var(--azul); border-color:var(--azul); color:#fff; }
        .obrigatorio { color:#EF4444; font-size:.7rem; font-weight:700; margin-left:4px; text-transform:none; }
        .fotos-grid { display:flex; flex-wrap:wrap; gap:10px; margin-top:4px; }
        .foto-thumb { position:relative; width:88px; height:88px; border-radius:8px; overflow:hidden; border:1.5px solid #E5E7EB; }
        .foto-thumb img { width:100%; height:100%; object-fit:cover; cursor:pointer; }
        .foto-remove { position:absolute; top:4px; right:4px; background:rgba(0,0,0,.6); color:#fff; border:none; border-radius:50%; width:20px; height:20px; display:flex; align-items:center; justify-content:center; cursor:pointer; }
        .foto-add { width:88px; height:88px; border-radius:8px; border:1.5px dashed #CBD5E1; background:#F8FAFC; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px; cursor:pointer; color:#94A3B8; font-size:.72rem; font-weight:500; }
        .foto-add:hover { border-color:var(--azul); color:var(--azul); }
        @media(max-width:640px){ .page{padding:16px;} .form-grid{grid-template-columns:1fr;} .form-full{grid-column:1;} .tipo-toggle{flex-wrap:wrap;} }
      `}</style>
    </div>
  );
}
